"use client";

import { useRef, useState } from "react";
import {
  isRouteDataState,
  type RouteDataState,
} from "@/app/lib/routeStateContract";
import { EventsDirectoryView } from "./EventsDirectoryView";
import {
  DEFAULT_EVENT_FILTERS,
  EVENT_DIRECTORY_FIXTURES,
  INTRAMURAL_EVENT_SOURCES,
  type EventTab,
  type SportItem,
} from "./eventsDirectoryViewModel";
import type { EventFilters } from "@/app/types/events";
import { INTRAMURAL_GAME_FIXTURES } from "./components/EventsIntramuralPanel";

export type EventsFixtureMode =
  | RouteDataState
  | "tab"
  | "sport"
  | "search"
  | "filter"
  | "rooms"
  | "intramural"
  | "mobile-filter"
  | "long";
export function EventsAuditFixture({ state, embedded = false }: { state: EventsFixtureMode; embedded?: boolean }) {
  const observerTarget = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<EventFilters>(
    state === "search"
      ? { ...DEFAULT_EVENT_FILTERS, search: "workshop" }
      : state === "filter"
        ? { ...DEFAULT_EVENT_FILTERS, dateRange: "week" }
        : state === "rooms"
          ? {
              sources: [],
              dateRange: "all",
              search: "",
              category: "Study Space",
            }
          : state === "intramural"
            ? {
                sources: INTRAMURAL_EVENT_SOURCES,
                dateRange: "all",
                search: "",
                category: null,
              }
            : DEFAULT_EVENT_FILTERS,
  );
  const activeTab: EventTab =
    state === "tab" || state === "sport"
      ? "rutgers"
      : state === "rooms"
        ? "rooms"
        : state === "intramural"
          ? "intramural"
          : "clubs";
  const activeSport = state === "sport" ? "basketball" : null;
  const loading = state === "initial-loading";
  const error =
    state === "hard-failure"
      ? "Failed to load events. Check your connection or try again."
      : null;
  const retainsData = [
    "refreshing",
    "stale-cache",
    "partial",
    "permission",
  ].includes(state);
  const events =
    (state === "empty" || Boolean(error) || loading) && !retainsData
      ? []
      : state === "long"
        ? EVENT_DIRECTORY_FIXTURES.long
        : EVENT_DIRECTORY_FIXTURES.populated;
  return (
    <div
      data-audit-ready={isRouteDataState(state) ? `events-${state}` : undefined}
      data-audit-state={isRouteDataState(state) ? state : undefined}
      data-audit-fixture-heading
    >
      <EventsDirectoryView
        embedded={embedded}
        events={events}
        loading={loading}
        error={error}
        hasMore={state === "long"}
        totalCount={events.length || (loading || Boolean(error) ? null : 0)}
        activeTab={activeTab}
        activeSport={activeSport}
        filters={filters}
        showMobileFilters={state === "mobile-filter"}
        observerTarget={observerTarget}
        onSelectTab={() => undefined}
        onSelectSport={(_tab: EventTab, _sport: SportItem) => undefined}
        onSetFilters={setFilters}
        onSearchChange={(search) =>
          setFilters((current) => ({ ...current, search }))
        }
        onOpenFilters={() => undefined}
        onCloseFilters={() => undefined}
        onRetry={() => undefined}
        fixtureState={isRouteDataState(state) ? state : undefined}
        intramuralFixtureGames={
          state === "intramural" ? INTRAMURAL_GAME_FIXTURES : undefined
        }
      />
    </div>
  );
}
